import type { ListBuilderConfiguration } from '../types';

/**
 * PD-14192 — Ownerships management list configuration. Drives the list-builder
 * Ownerships page (replacing the interim management sidebar). Mirrors the
 * client-lists config: a name + description column, an account-count column, a
 * created-date column, and an actions column (edit + delete). The objects
 * baseUrl points at the future ownership detail page (/clients/ownerships/:id).
 */
export const OWNERSHIP_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-ownerships-all',
			cacheTTL: 600000,
			name: 'All Ownerships',
			type: 'default',
			active: true,
			columns: [
				{
					id: 'ownership-name',
					fields: [
						{
							id: 'name-field',
							// PD-14228: the ownership name links to its detail page
							// (/clients/ownerships/:id, from the objects baseUrl below).
							link: {
								type: 'object',
								enabled: true,
								objectTable: 'ownership',
								idField: '_id',
							},
							dataConfig: {
								type: 'field',
								field: { fieldName: 'name', objectTable: 'ownership' },
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-1',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'line_break',
						},
						{
							id: 'description-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'description', objectTable: 'ownership' },
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h1', text: 'Ownership' }],
					minWidth: 200,
					sortable: true,
					alignment: 'left',
					sortFields: [
						{ direction: 'ASC', fieldName: 'name', objectTable: 'ownership' },
					],
					widthConfig: { mode: 'flexible', preset: 'large' },
					verticalAlignment: 'top',
				},
				{
					id: 'account-count',
					fields: [
						{
							id: 'account-count-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'clientCount', objectTable: 'ownership' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h2', text: '# Accounts' }],
					sortable: true,
					alignment: 'center',
					sortFields: [
						{
							direction: 'DESC',
							fieldName: 'clientCount',
							objectTable: 'ownership',
						},
					],
					widthConfig: { mode: 'fixed', width: 100 },
					verticalAlignment: 'top',
				},
				{
					id: 'created-date',
					fields: [
						{
							id: 'created-date-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: '_created_date',
									objectTable: 'ownership',
								},
							},
							styleConfig: {
								// PD-14228: match other lists' primary column text (white),
								// not the secondary gray used for sub-line detail.
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'date',
						},
					],
					headers: [{ id: 'h3', text: 'Created' }],
					sortable: true,
					alignment: 'left',
					sortFields: [
						{
							direction: 'DESC',
							fieldName: '_created_date',
							objectTable: 'ownership',
						},
					],
					widthConfig: { mode: 'fixed', width: 120 },
					verticalAlignment: 'top',
				},
				{
					id: 'actions',
					fields: [
						{
							id: 'actions-dropdown',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							// Row actions render as the standard actions dropdown (the
							// list-builder's supported renderer); callbackName values are
							// matched by the page's callbacks map.
							displayStyle: 'actions_dropdown',
							dropdownActions: [
								{
									id: 'edit-action',
									label: 'Edit',
									iconName: 'pen',
									callbackName: 'editOwnership',
									variant: 'primary',
									visibility: 'visible',
									requiredPermission: 'clients:detail:any',
								},
								{
									id: 'delete-action',
									label: 'Delete',
									iconName: 'trash',
									callbackName: 'deleteOwnership',
									variant: 'danger',
									visibility: 'visible',
									requiredPermission: 'clients:detail:any',
								},
							],
						},
					],
					headers: [{ id: 'h4', text: 'Actions' }],
					sortable: false,
					alignment: 'right',
					widthConfig: { mode: 'fixed', width: 120 },
					verticalAlignment: 'top',
				},
			],
			description: 'All ownerships',
			defaultSortId: 'name-asc',
			requiredFields: [
				'ownership._id',
				'ownership.name',
				'ownership.description',
				'ownership.clientCount',
				'ownership._created_date',
			],
			sortOrder: 0,
		},
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [25, 50, 100],
	},
	filters: {
		activeFilters: [],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/clients/ownerships/:id',
			objectLabel: 'Ownership',
			objectTable: 'ownership',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'name', fieldLabel: 'Name' },
				{ fieldName: 'description', fieldLabel: 'Description' },
				{ fieldName: 'clientCount', fieldLabel: 'Account Count' },
				{ fieldName: '_created_date', fieldLabel: 'Created Date' },
			],
		},
	],
	tableSorts: [
		{
			id: 'name-asc',
			name: 'Name (A-Z)',
			fields: [
				{ direction: 'ASC', fieldName: 'name', objectTable: 'ownership' },
			],
			description: 'Sort ownerships by name',
		},
		{
			id: 'name-desc',
			name: 'Name (Z-A)',
			fields: [
				{ direction: 'DESC', fieldName: 'name', objectTable: 'ownership' },
			],
			description: 'Sort ownerships by name descending',
		},
		{
			id: 'account-count-desc',
			name: 'Most Accounts',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'clientCount',
					objectTable: 'ownership',
				},
			],
			description: 'Sort by number of accounts',
		},
		{
			id: 'created-desc',
			name: 'Newest First',
			fields: [
				{
					direction: 'DESC',
					fieldName: '_created_date',
					objectTable: 'ownership',
				},
			],
			description: 'Sort by creation date',
		},
	],
};
